/**
 * Customize Section - Kiosk Settings
 * Logo and Idle Screen configuration with drag-and-drop upload UI
 * Clean UI layer - all business logic in kiosk-settings-store
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import { useKioskSettingsStore } from '../../../stores/kiosk-settings-store';
import { LogoUploadZone } from '@/features/device-customizer/core/components/shared/LogoUploadZone';
import { MediaUploadZone } from '@/features/device-customizer/core/components/shared/MediaUploadZone';
import { LogoCropDialog } from '@/features/device-customizer/core/components/shared/LogoCropDialog';
import { createFilePreviewUrl } from '@/features/device-customizer/core/utils/file-helpers';
import { Switch } from '@/core/components/ui/switch';
import { Label } from '@/core/components/ui/label';
import { Input } from '@/core/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/core/components/ui/tooltip';

interface CustomizeSectionProps {
  isOpen: boolean;
  onToggle: () => void;
}

export const CustomizeSection = ({ isOpen, onToggle }: CustomizeSectionProps) => {
  const { t } = useTranslation('assignEv');
  const {
    settings,
    isUploading,
    updateField,
    updateNestedField,
    pendingLogo,
    setPendingLogo,
    clearPendingLogo,
    pendingIdleScreen4,
    setPendingIdleScreen4,
    clearPendingIdleScreen4,
    pendingIdleScreen5,
    setPendingIdleScreen5,
    clearPendingIdleScreen5,
    toggleIdleScreenPreview,
  } = useKioskSettingsStore();

  const [showLogoCropDialog, setShowLogoCropDialog] = useState(false);
  const [showIdleScreen4CropDialog, setShowIdleScreen4CropDialog] = useState(false);
  const [showIdleScreen5CropDialog, setShowIdleScreen5CropDialog] = useState(false);

  // Logo handlers
  const handleLogoSelect = useCallback(
    (file: File) => {
      const previewUrl = createFilePreviewUrl(file);
      setPendingLogo({ file, originalImageUrl: previewUrl, previewUrl });
    },
    [setPendingLogo]
  );

  const handleLogoRemove = useCallback(() => {
    clearPendingLogo();
    updateField('logo', null);
  }, [clearPendingLogo, updateField]);

  const handleLogoCrop = useCallback(() => {
    setShowLogoCropDialog(true);
  }, []);

  // Idle screen 4-inch handlers
  const handleIdleScreen4Select = useCallback(
    (file: File) => {
      const previewUrl = createFilePreviewUrl(file);
      setPendingIdleScreen4({
        file,
        mediaType: file.name.endsWith('.json') ? 'lottie' : 'image',
        originalImageUrl: previewUrl,
        previewUrl,
      });
    },
    [setPendingIdleScreen4]
  );

  const handleIdleScreen4Remove = useCallback(() => {
    clearPendingIdleScreen4();
    updateNestedField('idleScreen', 'media4Url', null);
  }, [clearPendingIdleScreen4, updateNestedField]);

  const handleIdleScreen4Crop = useCallback(() => {
    setShowIdleScreen4CropDialog(true);
  }, []);

  // Idle screen 5-inch handlers
  const handleIdleScreen5Select = useCallback(
    (file: File) => {
      const previewUrl = createFilePreviewUrl(file);
      setPendingIdleScreen5({
        file,
        mediaType: file.name.endsWith('.json') ? 'lottie' : 'image',
        originalImageUrl: previewUrl,
        previewUrl,
      });
    },
    [setPendingIdleScreen5]
  );

  const handleIdleScreen5Remove = useCallback(() => {
    clearPendingIdleScreen5();
    updateNestedField('idleScreen', 'imageUrl', null);
  }, [clearPendingIdleScreen5, updateNestedField]);

  const handleIdleScreen5Crop = useCallback(() => {
    setShowIdleScreen5CropDialog(true);
  }, []);

  if (!settings) return null;
  const idleScreen = settings.idleScreen || {};
  const timeoutSeconds = idleScreen.timeout != null ? idleScreen.timeout / 1000 : 30;
  const isTimeoutInvalid = timeoutSeconds < 5 || timeoutSeconds > 60;

  return (
    <div className="rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      {/* Section Header */}
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-100 hover:bg-gray-200 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isOpen ? (
            <ChevronDown className="w-5 h-5 text-gray-700" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-700" />
          )}
          <h3 className="font-bold text-gray-900 text-base">{t('kiosk.customizeSection.title')}</h3>
        </div>
      </button>

      {/* Section Content */}
      {isOpen && (
        <div className="px-4 py-3 bg-white space-y-6">
          {/* Logo Upload */}
          <div>
            <h4 className="text-sm font-semibold mb-2 text-gray-700">{t('kiosk.customizeSection.logo.title')}</h4>
            <p className="text-xs text-gray-500 mb-3">{t('kiosk.customizeSection.logo.description')}</p>
            <LogoUploadZone
              label={t('kiosk.customizeSection.logo.uploadLabel')}
              target="media4"
              currentLogo={
                settings.logo
                  ? {
                      assetId: 'kiosk-settings-logo',
                      s3Url: settings.logo,
                      localFileName: 'logo.png',
                      contentType: 'image/png',
                      size: 0,
                    }
                  : undefined
              }
              pendingLogo={pendingLogo}
              disabled={isUploading}
              onFileSelect={handleLogoSelect}
              onRemove={handleLogoRemove}
              onCrop={handleLogoCrop}
            />
          </div>

          {/* Idle Screen */}
          <div className="pt-5 space-y-4">
            {/* Enabled toggle */}
            <div className="flex items-center gap-3">
              <Switch
                id="idleScreenEnabled"
                checked={idleScreen.enabled || false}
                onCheckedChange={(checked) => {
                  updateNestedField('idleScreen', 'enabled', checked);
                  if (checked) updateNestedField('idleScreen', 'timeout', 30000);
                  toggleIdleScreenPreview(checked);
                }}
              />
              <Label htmlFor="idleScreenEnabled" className="text-sm font-semibold cursor-pointer">
                Idle Screen
              </Label>
            </div>

            {/* Back to Idle Screen After - only when enabled */}
            {idleScreen.enabled && (
              <div>
                <div className="flex items-center gap-1 mb-2">
                  <p className="text-xs text-gray-500">Back to Idle Screen (in seconds)</p>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 cursor-help shrink-0" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs text-xs">
                        Time in seconds of inactivity before the kiosk returns to the idle screen. Default: 30 seconds.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Input
                  type="number"
                  min={5}
                  max={60}
                  step={5}
                  value={timeoutSeconds}
                  onChange={(e) => updateNestedField('idleScreen', 'timeout', Number(e.target.value) * 1000)}
                  className={`w-28 bg-white ${isTimeoutInvalid ? 'border-red-500 focus-visible:ring-red-500' : 'border-gray-300'}`}
                />
                {isTimeoutInvalid && (
                  <p className="text-xs text-red-500 mt-1">Value must be between 5 and 60</p>
                )}
              </div>
            )}

            {/* Media uploads - only when enabled */}
            {idleScreen.enabled && (
              <div className="space-y-4">

                {/* 4-inch Idle Screen Upload */}
                <div>
                  <h4 className="text-sm font-medium mb-3 text-gray-700">4-inch Display</h4>
                  <MediaUploadZone
                    label="4-inch Idle Screen"
                    target="media4"
                    currentMedia={
                      (idleScreen as Record<string, unknown>).media4Url
                        ? {
                            assetId: 'kiosk-idle-4inch',
                            s3Url: (idleScreen as Record<string, unknown>).media4Url as string,
                            localFileName: 'idle-4inch.png',
                            contentType: 'image/png',
                            size: 0,
                          }
                        : undefined
                    }
                    pendingMedia={pendingIdleScreen4}
                    disabled={isUploading}
                    onFileSelect={handleIdleScreen4Select}
                    onRemove={handleIdleScreen4Remove}
                    onCrop={handleIdleScreen4Crop}
                    helpText="Recommended: 480x480px, PNG/JPG/SVG/WebP (<2MB) or Lottie JSON (<1MB)"
                  />
                </div>

                {/* 5-inch Idle Screen Upload */}
                <div>
                  <h4 className="text-sm font-medium mb-3 text-gray-700">5-inch Display</h4>
                  <MediaUploadZone
                    label="5-inch Idle Screen"
                    target="media5"
                    currentMedia={
                      idleScreen.imageUrl
                        ? {
                            assetId: 'kiosk-idle-5inch',
                            s3Url: idleScreen.imageUrl,
                            localFileName: 'idle-5inch.png',
                            contentType: 'image/png',
                            size: 0,
                          }
                        : undefined
                    }
                    pendingMedia={pendingIdleScreen5}
                    disabled={isUploading}
                    onFileSelect={handleIdleScreen5Select}
                    onRemove={handleIdleScreen5Remove}
                    onCrop={handleIdleScreen5Crop}
                    helpText="Recommended: 720x1280px, PNG/JPG/SVG/WebP (<2MB) or Lottie JSON (<1MB)"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Logo Crop Dialog */}
      {showLogoCropDialog && pendingLogo && (
        <LogoCropDialog
          open={showLogoCropDialog}
          onCancel={() => setShowLogoCropDialog(false)}
          imageUrl={pendingLogo.originalImageUrl}
          onConfirm={(_croppedBlob, _cropArea) => {
            setShowLogoCropDialog(false);
          }}
          initialCropArea={pendingLogo.cropArea}
          title="Crop Logo"
        />
      )}

      {/* Idle Screen 4-inch Crop Dialog */}
      {showIdleScreen4CropDialog && pendingIdleScreen4 && pendingIdleScreen4.mediaType === 'image' && (
        <LogoCropDialog
          open={showIdleScreen4CropDialog}
          onCancel={() => setShowIdleScreen4CropDialog(false)}
          imageUrl={pendingIdleScreen4.originalImageUrl}
          onConfirm={(_croppedBlob, _cropArea) => {
            setShowIdleScreen4CropDialog(false);
          }}
          initialCropArea={pendingIdleScreen4.cropArea}
          title="Crop 4-inch Idle Screen"
        />
      )}

      {/* Idle Screen 5-inch Crop Dialog */}
      {showIdleScreen5CropDialog && pendingIdleScreen5 && pendingIdleScreen5.mediaType === 'image' && (
        <LogoCropDialog
          open={showIdleScreen5CropDialog}
          onCancel={() => setShowIdleScreen5CropDialog(false)}
          imageUrl={pendingIdleScreen5.originalImageUrl}
          onConfirm={(_croppedBlob, _cropArea) => {
            setShowIdleScreen5CropDialog(false);
          }}
          initialCropArea={pendingIdleScreen5.cropArea}
          title="Crop 5-inch Idle Screen"
        />
      )}
    </div>
  );
};
